// WordDoors — the chip's shape (radius, padding, the round pill silhouette) is `@import`ed from
// `_chipDress.scss`'s `@mixin chip-dress` (never composed cross-file — see that file's own header
// for why), the same mixin `RatioFold`'s own ratio chip includes (board `WordsAsDoors`, validated
// 2026-09-18: "the doors are CHIPS in the ratio chip's dress" — the shape, not a byte-identical
// copy: a word door's own ground, case and border diverge from the ratio chip's, operator-corrected
// 2026-09-19, `.chip`'s own doc below has the reasoning for each).
@import './chipDress';

// A word-door chip's own rendered height, fixed here rather than left to inherit an ambient
// line-height that varies by house (`--tl-font`'s own 1.65, Toile has no global box-sizing reset —
// `.chip` below pins both `box-sizing` and `line-height` so this number is exact): 2 * 6px padding
// + 10px content line (font-size 10, line-height 1) + 2 * 1px border. `.chips`' own clip height
// reads the SAME variable, so the row can never clip a line shorter or taller than the chip it
// holds — one number, not two kept equal by a comment.
$chip-h: 24px;
// The sheet surface's own tap-target floor (`_chipDress.scss`'s `$tap-floor`, the same number
// `RatioFold.module.scss`'s own sheet rule reads) is this chip's exact height on that surface too
// — `.chips`' clip height follows it, below.
$chip-h-sheet: $tap-floor;

// The outer row: the chip cluster, then the discreet ↗ as a fixed-width sibling that is never
// itself the thing a narrow `.chips` clips away. The gap here — a chip's own inter-chip gap is
// `.chips`' own, below, 6px — is roughly double that (operator, 2026-09-19: "a larger gap between
// the last chip and the ↗ than between chips… so it does not read as the row's last action").
// `min-width: 0` only matters when a caller bounds this row to a flex ancestor of its own
// (`Tile.tsx`'s `.tileChips`) — harmless where nothing does (Column/Sheet, which centre a naturally
// shrink-to-fit row).
.row {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

// The discreet pack-page link. `[data-product='toile'] a { color: inherit }`
// (products/toile/styles/index.scss) used to compile to specificity (0,1,1) — the attribute
// selector counts as a class — which outranked this bare `.arrow` rule at (0,1,0) and rendered the
// arrow in the inherited (near-white) body colour regardless of what this rule asked for (operator-
// observed, 2026-09-19: "renders near-white"). Fixed at the source: that global rule now wraps its
// scope in `:where()`, the same cure Ovid already carries for the identical bug class — a plain
// single-class rule here is enough again.
.arrow {
    flex: none;
    font-size: 9px;
    color: var(--tl-dim);
    text-decoration: none;
    line-height: 1;
    transition: color 150ms ease;

    &:hover,
    &:focus-visible {
        color: var(--tl-muted);
    }
}

// The tile's own hover-label arrow (`WordDoors.tsx`'s `arrowReveal: 'focus'`, `ARROW_FOCUS_ONLY_
// CLASS`): hidden and pointer-inert by default — `Tile.module.scss`'s own `:focus-within` override
// is the only rule that raises it, since only that file knows the tile's own hover/focus state (the
// cross-file contract `WordDoors.tsx`'s own doc states in full). Column/Sheet's arrow never carries
// this class, so it never reaches this rule at all.
:global(.wall-arrow-focus-only) {
    opacity: 0;
    pointer-events: none;
}

// The chip cluster — an ordinary wrapping flex row pinned to exactly one chip's own height
// (`$chip-h`) with `overflow: hidden`: a chip that does not fit the first line wraps to a second,
// which the fixed height clips whole (`WordDoors.tsx`'s own header doc has the full rationale).
// `flex: 1 1 auto` + `min-width: 0` is what lets this shrink and wrap once `.row` itself is bounded
// (Tile); with nothing bounding `.row` (Column/Sheet) a flex item's intrinsic contribution is its
// own content size regardless of `flex-grow`, so this stays shrink-to-fit and single-line there —
// the same rule that already let `Tile.module.scss`'s `.wordsGroup` sit beside a from-scratch
// cell's Generate door without a second, caller-specific override.
.chips {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: 6px;
    overflow: hidden;
    max-height: $chip-h;
}

// `@include chip-dress` inlines the shared declarations into THIS rule at compile time (a Sass
// mixin, not a second CSS class) — the properties below it are later in the SAME rule, so they win
// outright by CSS's own last-declaration-wins, regardless of anything about bundle or import order.
//
// A word door's own dress diverges from the ratio chip it borrows the mixin from (operator,
// 2026-09-19: "the board wins over the production ratio chip you copied… over the scrimmed wall the
// hairline border vanishes and the words read weakly — a control over imagery carries its own
// ground"): UPPERCASE (harmless on the ratio chip's digits, so that half stayed in the shared
// mixin), tighter letter-spacing, an opaque-ish ground of its own, and a border the wall's own
// muted ink can still draw at 40% rather than the ratio chip's `--tl-border-strong` (tuned for the
// bar's own opaque ground, not a photograph). None of this touches `_chipDress.scss` — `text-
// transform`/size/spacing would visibly change the shipped ratio chip's "3:4" too, so the shared
// mixin stays the ratio chip's own, untouched, and every word-door addition lives here instead.
.chip {
    @include chip-dress;
    box-sizing: border-box;
    line-height: 1;
    height: $chip-h;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    background: color-mix(in srgb, var(--tl-stage) 72%, transparent);
    border-color: color-mix(in srgb, var(--tl-muted) 40%, transparent);

    // A chip whose phrase is a pill in the bar (`aria-pressed`).
    // Neutral ink, as the pill: tungsten is the verb's.
    &[aria-pressed='true'] {
        color: var(--tl-text);
        border-color: var(--tl-text);
    }

    // A chip is never wider than its row (operator, second look: "AMETHYST CLUS" sliced flat, no
    // right end to the pill, on a narrow tile — the first chip in a row cannot wrap away the way a
    // second one can, and `chip-dress`'s own `flex: none` refuses to shrink it). `max-width: 100%`
    // clamps a flex item's rendered size even with flex-shrink 0 (the flex-basis clamp happens
    // before the shrink factor is applied) — an over-wide chip truncates INSIDE its own pill
    // (border and rounded end intact, `box-sizing: border-box` above keeps them inside the clamp)
    // instead of overflowing `.chips`' own clip and being cut off flat. `min-width: 0` only matters
    // here to disable the automatic min-content floor; `overflow: hidden` (needed for the ellipsis)
    // already implies it, but stated for clarity. A chip that fits keeps its natural width — this
    // only ever engages for the one chip on a wrapped line still too wide for the row, so every
    // other chip still wraps and drops whole exactly as before.
    max-width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;

    &:hover,
    &:focus-visible,
    &:active {
        color: var(--tl-text);
        border-color: color-mix(in srgb, var(--tl-accent) 45%, transparent);
    }

    // The column caption and the sheet cap read at one size up from a tile's hover label (operator,
    // 2026-09-19: "11px in the column caption and sheet cap"); the hover label keeps `chip-dress`'s
    // own 10px, which the strip's own tighter width still needs. `&.chipCaption` (two classes) wins
    // over `.chip` alone regardless of which rule the compiler places first — no ordering to keep.
    &.chipCaption {
        font-size: 11px;
    }
}

// The sheet's own tap-target floor (`RatioFold.module.scss`'s own rule clamps ITS `.chip` up to
// `$tap-floor` via `min-height`; this file's `.chip` is a separate class since `chip-dress` is
// `@include`d, not composed, so it needs the same floor stated for itself) — `.chips`' clip height
// already reads `$chip-h-sheet` (== `$tap-floor`) above, so the two can never drift apart.
:global(html[data-wall-surface='sheet']) {
    .chips {
        max-height: $chip-h-sheet;
    }

    .chip {
        min-width: $tap-floor;
        height: $chip-h-sheet;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
}
